import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Copy, Check, X } from 'lucide-react'
import type { DomainStatus } from '@/api/emailDomain'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import {
  useSendingDomain,
  useSetSendingDomain,
  useVerifySendingDomain,
  useRemoveSendingDomain,
} from '@/features/settings/useSendingDomain'

const STATUS_STYLE: Record<DomainStatus, { label: string; cls: string }> = {
  none: { label: 'Not set', cls: 'bg-gray-100 text-gray-600' },
  pending: { label: 'Pending DNS', cls: 'bg-amber-100 text-amber-700' },
  verified: { label: 'Verified', cls: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
}

function StatusBadge({ status }: { status: DomainStatus }) {
  const s = STATUS_STYLE[status]
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text)
        setCopied(true)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 1500)
      }}
      className="text-gray-400 hover:text-brand-primary"
      aria-label="Copy"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

export default function SendingDomainTab() {
  const query = useSendingDomain()
  const setDomain = useSetSendingDomain()
  const verify = useVerifySendingDomain()
  const remove = useRemoveSendingDomain()

  const [domainInput, setDomainInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (query.isLoading) {
    return <p className="text-sm text-gray-500">Loading…</p>
  }
  if (query.isError || !query.data) {
    return <p className="text-sm text-red-600">Could not load sending-domain settings.</p>
  }

  const { domain, status, dnsRecords } = query.data

  function submitDomain(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setDomain.mutate(domainInput, { onError: (err) => setError(apiErrorMessage(err)) })
  }

  return (
    <div className="max-w-3xl">

      {!domain ? (
        <form onSubmit={submitDomain} className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Your email domain</label>
            <input
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="yourdomain.com"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={setDomain.isPending || !domainInput.trim()}
            className="px-4 py-2 rounded bg-brand-primary text-white text-sm font-medium disabled:opacity-50"
          >
            {setDomain.isPending ? 'Setting up…' : 'Authenticate domain'}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-brand-darkest">{domain}</span>
            <StatusBadge status={status} />
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">
              Add these CNAME records to your DNS, then click <b>Check verification</b>. It can take
              up to ~48h after the records propagate.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Name / Host</th>
                    <th className="text-left px-3 py-2">Value</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dnsRecords.map((r) => (
                    <tr key={r.host} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono">{r.type}</td>
                      <td className="px-3 py-2 font-mono">
                        <span className="inline-flex items-center gap-1">
                          {r.host} <CopyButton text={r.host} />
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <span className="inline-flex items-center gap-1">
                          {r.value} <CopyButton text={r.value} />
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {r.valid ? (
                          <Check size={14} className="text-green-600" aria-label="Verified" />
                        ) : (
                          <X size={14} className="text-gray-400" aria-label="Not verified yet" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
              className="px-4 py-2 rounded bg-brand-primary text-white text-sm font-medium disabled:opacity-50"
            >
              {verify.isPending ? 'Checking…' : 'Check verification'}
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="px-4 py-2 rounded border border-gray-300 text-gray-600 text-sm disabled:opacity-50"
            >
              Change domain
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
    </div>
  )
}
